import ko from "knockout";
import componentCache from "flow/utils/componentCache";
import eventSystem from "flow/events/eventSystem";
import config from "flow/config";
import "flow/viewModels/DefaultWorkingContextViewModel";
import "flow/viewModels/HomeContext";
import ajaxErrorHelper from "flow/ajax/ajaxErrorHelper";
import deferred from "flow/promise/deferred";
import { Dictionary } from "flow/api/types";
import Component from "flow/Component";
import { CanLeaveEntry } from "flow/Viewport";

export class WorkingContext {
    isBroken = ko.observable<boolean>(false);
    isExpanded = ko.observable<boolean>(false);
    currentComponent = ko.observable<Component>(null);

    /** @deprecated */
    component = this.currentComponent;

    currentId = ko.pureComputed<string>(() => {
        if (this.currentComponent()) {
            return this.currentComponent().manifest.id;
        }
        return "";
    });

    isHomeScreen = ko.pureComputed<boolean>(() => this.currentId() === config.componentIds.homeContext);

    hasExpander = ko.pureComputed<boolean>(() => {
        if (
            this.currentComponent() !== null &&
            this.currentComponent() !== undefined &&
            this.currentComponent().manifest.detailViewUrl !== undefined &&
            this.currentComponent().manifest.detailViewUrl !== null
        ) {
            return this.currentComponent().expanderWantsVisibility();
        } else {
            return false;
        }
    });

    hasNotifications = ko.pureComputed<boolean>(() => !!this.currentComponent());

    isBackgroundLoading = ko.observable<boolean>(false);

    hasContext = ko.pureComputed<boolean>(() =>
        this.currentComponent() && this.currentComponent().viewHtml()
            ? this.currentComponent().viewHtml().length > 0
            : false
    );

    isWorkflow = ko.pureComputed<boolean>(() =>
        this.currentComponent() ? this.currentComponent().manifest.isWorkflow : false
    );

    isLoading = ko.pureComputed<boolean>(
        () => (this.currentComponent() ? this.currentComponent().isLoading() : true) && this.isBackgroundLoading()
    );

    isHomeOrFunction = ko.pureComputed<boolean>(() => {
        return (
            this.currentId() === config.componentIds.homeContext ||
            this.currentId() === config.componentIds.defaultWorkingContext
        );
    });

    canLeave(id: string): CanLeaveEntry[] {
        if (this.currentComponent() !== null) {
            const canLeaveResult = this.currentComponent().canLeave(id);

            if (canLeaveResult) {
                return [{ component: this.currentComponent(), result: canLeaveResult }];
            }
        }

        return [];
    }

    loadComponentById(id: string, viewContext: Dictionary, selectedContainerId: string): JQueryPromise<any> {
        this.isBackgroundLoading(true);

        const component = componentCache.getComponentById(id);

        if (this.currentComponent() !== null && this.currentComponent() !== component) {
            this.currentComponent().onHide();

            if (this.currentComponent().manifest.reuseInstance === false) {
                this.currentComponent().onDestroy();
            }

            this.currentComponent(null);
        } else if (this.currentComponent() !== null && this.currentComponent() === component) {
            this.currentComponent().onHide();
        }

        const dfd = deferred(component.myFlow);

        this.currentComponent(component);

        component
            .load()
            .done(() => {
                this.activateComponent(component, viewContext, selectedContainerId);
                component.onShow();
                dfd.resolve();
            })
            .fail(possibleJqXhr => {
                this.isBroken(true);
                this.isBackgroundLoading(false);
                if (
                    ajaxErrorHelper.isjqXhrObject(possibleJqXhr) &&
                    ajaxErrorHelper.isConnectionProblem(possibleJqXhr)
                ) {
                    component.myFlow.handleAjaxError(possibleJqXhr);
                } else {
                    component.myFlow.showErrorDialog(
                        component.myFlow.translate("FlowHtml.Translations.FailedToLoadWorkingContext"),
                        component.myFlow.translate("FlowHtml.Translations.PleaseTryAgainOrContactAdmin")
                    );
                }
                dfd.reject();
            });

        return dfd.promise();
    }

    activateComponent(component: Component, viewContext: Dictionary, selectedContainerId: string): void {
        eventSystem.publish(config.topics.workingContextChangeStarted, component?.manifest?.id);
        component.activate(viewContext, selectedContainerId).then((publishEvent: boolean) => {
            if (publishEvent !== false) {
                eventSystem.publish(config.topics.workingContextChanged, component?.manifest?.id);
            }
            this.isBackgroundLoading(false);
        });
    }

    clear(): void {
        if (this.currentId() !== config.componentIds.defaultWorkingContext) {
            this.loadComponentById(config.componentIds.defaultWorkingContext, undefined, undefined);
        }
    }

    onExpand(): void {
        this.isExpanded(!this.isExpanded());
        eventSystem.publish(config.topics.workingContextViewExpandedChanged, this.isExpanded());
    }

    shouldBeVisible(componentId: string, hint: any): boolean {
        return this.currentComponent() ? this.currentComponent().shouldBeVisible(componentId, hint) : false;
    }

    currentFunctionComponentId(): string {
        if (
            this.currentComponent() &&
            this.currentComponent().viewModel() &&
            this.currentComponent().viewModel().currentFunctionComponentId
        ) {
            return this.currentComponent().viewModel().currentFunctionComponentId();
        } else {
            return "";
        }
    }

    configureHome() {
        if (this.currentComponent()?.myFlow.manifest.id === config.componentIds.homeContext) {
            this.currentComponent().viewModel().configureHome();
        }
    }
}

const workingContext = new WorkingContext();
export default workingContext;
